import React, { useState } from 'react';
import {
  TableRow,
  TableCell,
  Chip,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';
import { VendorStatementDoc } from '../types';
import { updateDocument } from '../../api/document-api/DocumentAPI';
import { useUser } from '../../api/user-api/UserAPI';
import { DeleteConfirmationModal } from './DeleteConfirmationModal';

interface VendorStatementJobRowProps {
  job: VendorStatementDoc;
  users: Record<string, string>;
  onJobClick: (job: VendorStatementDoc) => void;
}

/**
 * Get color associated with job status
 * @param status - The status to get the color of
 * @returns The color of the status
 */
const getStatusColor = (status: string) => {
  switch (status) {
    case 'awaiting-ai':
      return 'warning';
    case 'ai-parsing-completed':
      return 'info';
    case 'job-completed':
      return 'success';
    default:
      return 'default';
  }
};

/**
 * Get label associated with job status
 * @param status - The status to get the label of
 * @returns The label of the status
 */
const getStatusLabel = (status: string) => {
  switch (status) {
    case 'awaiting-ai':
      return 'Awaiting AI';
    case 'ai-parsing-completed':
      return 'AI Parsing Completed';
    case 'job-completed':
      return 'Completed';
    default:
      return status;
  }
};

/**
 * Format date string to locale date string
 * @param dateString - The date string to format
 * @returns The formatted date string
 */
const formatDate = (dateString: string) => {
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'Invalid Date';
  }
};

/**
 * Row in the vendor statements table
 * @param job - The job to display
 * @param users - The users to display
 * @param onJobClick - The function to call when a job is clicked
 * @constructor
 */
// eslint-disable-next-line import/prefer-default-export
export const VendorStatementJobRow: React.FC<VendorStatementJobRowProps> = function ({
  job, users, onJobClick,
}) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const getUserName = (userId: string) => users[userId] || userId;
  const user = useUser();

  /**
   * Handle delete confirmation - mark the job as deleted
   */
  const handleDeleteConfirm = async () => {
    if (!user) return;
    setIsDeleting(true);
    try {
      const data: Partial<VendorStatementDoc> = {
        deletedAt: new Date().toISOString(),
        deletedBy: user.uid,
      };
      await updateDocument('vendor-statements', job.id, data);
      setDeleteDialogOpen(false);
      enqueueSnackbar('Vendor statement deleted successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Error deleting vendor statement', { variant: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  const jobIsCompleted = job.status === 'job-completed';

  // Only allow opening modal for jobs ready to be edited (parsing-completed) and jobs that are completed (view only)
  const allowClick = job.status === 'ai-parsing-completed' || job.status === 'job-completed';

  return (
    <>
      <TableRow
        hover
        onClick={() => {
          if (allowClick) {
            onJobClick(job);
          }
        }}
        sx={{
          cursor: allowClick ? 'pointer' : 'default',
          '&:hover': {
            backgroundColor: allowClick ? 'action.hover' : 'inherit',
          },
        }}
      >
        {/* Job ID */}
        <TableCell>
          <Typography variant="body2" fontWeight="medium">
            {job.id}
          </Typography>
        </TableCell>
        {/* Job Status */}
        <TableCell>
          <Chip
            label={getStatusLabel(job.status)}
            color={getStatusColor(job.status) as any}
            size="small"
          />
        </TableCell>
        {/* Created By */}
        <TableCell>
          <Typography variant="body2">
            {getUserName(job.createdBy)}
          </Typography>
        </TableCell>
        {/* Created At */}
        <TableCell>
          <Typography variant="body2">
            {formatDate(job.createdAt)}
          </Typography>
        </TableCell>
        {/* Completed At */}
        <TableCell>
          <Typography variant="body2">
            {job.completedAt ? formatDate(job.completedAt) : '-'}
          </Typography>
        </TableCell>
        {/* Completed By */}
        <TableCell>
          <Typography variant="body2">
            {job.completedBy ? getUserName(job.completedBy) : '-'}
          </Typography>
        </TableCell>
        {/* Job Data preview */}
        <TableCell>
          <Box>
            {job.data ? (
              <Box>
                <Typography variant="caption" display="block">
                  Statement Date:
                  {' '}
                  {job.data.statementDate}
                </Typography>
                <Typography variant="caption" display="block">
                  Total Amount: $
                  {job.data.totalAmountDue?.toFixed(2) || '0.00'}
                </Typography>
                <Typography variant="caption" display="block">
                  Line Items:
                  {' '}
                  {job.data.lineItems?.length || 0}
                  {job.data.lineItems?.some((item) => item.hidden)
                    && ` (${job.data.lineItems.filter((item) => item.hidden).length} hidden)`}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No data available
              </Typography>
            )}
          </Box>
        </TableCell>
        {/* Actions */}
        <TableCell>
          {/* Delete job button */}
          <IconButton
            color="error"
            size="small"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation(); // Prevent row click
              setDeleteDialogOpen(true);
            }}
            disabled={isDeleting}
          >
            <Delete />
          </IconButton>
        </TableCell>
      </TableRow>

      {/* Delete job confirmation modal */}
      <DeleteConfirmationModal
        open={deleteDialogOpen}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setDeleteDialogOpen(false);
        }}
        isDeleting={isDeleting}
        jobIsCompleted={jobIsCompleted}
      />
    </>
  );
};
